import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { id } from '@wonder-core-ng/interfaces/id.type';
import { Observable } from 'rxjs';
import { BI_CORE_CONFIG } from '../config';
import { INotification } from '../event-store/interfaces/event.interface';

/**
 * The in-app notifications an action of type `SEND_NOTIFICATION` writes.
 *
 * Lives with the store rather than in `EventApiService`: notifications are read from the header bell
 * on every screen, so they went from being one tab of the events admin to a cross-cutting resource.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationApiService {
  private readonly httpClient = inject(HttpClient);
  private readonly config = inject(BI_CORE_CONFIG);

  private readonly route = `${this.config.apiBaseUrl}/notifications`;

  /**
   * Fetches the latest notifications, read ones included.
   *
   * Read and unread come back together on purpose: the bell needs the unread and the full view needs
   * both, and one 50-row response feeding both beats two endpoints that can disagree.
   */
  loadNotifications(tenantId: id, limit = 50): Observable<INotification[]> {
    return this.httpClient.get<INotification[]>(this.route, {
      headers: this.headers(tenantId),
      params: { limit },
    });
  }

  markNotificationRead(notificationId: number, tenantId: id): Observable<INotification> {
    return this.httpClient.patch<INotification>(
      `${this.route}/${notificationId}/read`,
      {},
      { headers: this.headers(tenantId) },
    );
  }

  markAllNotificationsRead(tenantId: id): Observable<{ updated: number }> {
    return this.httpClient.patch<{ updated: number }>(
      `${this.route}/read-all`,
      {},
      { headers: this.headers(tenantId) },
    );
  }

  private headers(tenantId: id): { [header: string]: string } {
    return { 'x-tenant-id': `${tenantId}` };
  }
}
